import { ApolloLink } from 'apollo-link';

import { confluenceLocalStorageInstance as localStorage, keys } from '@confluence/storage-manager';

// TODO: in the future, map all available query keys/responses programatically
export const QUERY_OVERRIDE_KEYS = {
	IS_NEW_USER: 'Query.isNewUser',
	IS_SITE_ADMIN: 'Query.isSiteAdmin',
	IS_ESTABLISHED_USER: 'ExperimentalUserPersonalization.isEstablishedUser',
	IS_MIGRATED_USER: 'quickstart-state:isMigratedUserV2',
	ORIGIN_PRODUCT: 'onboarding-origin-state:attributes',
};

const applyQueryOverrides = (
	response: Record<string, any>,
	overrides: Record<string, any>,
): void => {
	function deepApplyQueryOverrides(data: typeof response, typename?: string) {
		if (data == null || typeof data !== 'object') return data;

		if (!typename) {
			typename = data.__typename;
			if (!typename) return data;
		}

		data = shallowApplyQueryOverrides(data, typename);

		Object.entries(data).forEach(([key, value]) => (data[key] = deepApplyQueryOverrides(value)));

		return data;
	}

	const overrideEntries = Object.entries(overrides);

	function shallowApplyQueryOverrides(data: typeof response, typename: string) {
		const typenamePrefix = `${typename}.`;

		overrideEntries.forEach(([overrideKey, overrideValue]) => {
			if (!overrideKey.startsWith(typenamePrefix)) return;

			const field = overrideKey.substring(typenamePrefix.length);

			if (!(field in data)) return;

			// Match the type of the override to the type of the field:
			switch (typeof data[field]) {
				case 'boolean':
					overrideValue = overrideValue === 'true';
					break;
				case 'number':
					overrideValue = Number(overrideValue);
					break;
			}

			data[field] = overrideValue;
		});

		return data;
	}

	deepApplyQueryOverrides(response.data, 'Query');
};

const applyMigratedUserOverride = (
	{ data: { experimentalSitePersonalization } }: Record<string, any>,
	overrides: Record<string, any>,
): void => {
	// The useMigratedUser hook recomputes the state as false if these traits are not present
	if (
		experimentalSitePersonalization?.__typename === 'ExperimentalSitePersonalization' &&
		QUERY_OVERRIDE_KEYS.IS_MIGRATED_USER in overrides
	) {
		const keys = ['confluence_migration_status', 'confluence_first_migration_signal_date'];
		const filteredAttributes = experimentalSitePersonalization.attributes.filter(
			(i: any) => !keys.includes(i.name),
		);

		if (experimentalSitePersonalization?.attributes) {
			experimentalSitePersonalization.attributes = [
				...filteredAttributes,
				{
					name: 'confluence_migration_status',
					value: (overrides[QUERY_OVERRIDE_KEYS.IS_MIGRATED_USER] === 'true').toString(),
					__typename: 'ExperimentalSitePersonalizationAttributes',
				},
				{
					name: 'confluence_first_migration_signal_date',
					value: new Date().toISOString(),
					__typename: 'ExperimentalSitePersonalizationAttributes',
				},
			];
		}
	}
};

const applyEstablishedUserOverride = (
	{ data: { experimentalUserPersonalization } }: Record<string, any>,
	overrides: Record<string, any>,
): void => {
	let isEstablishedUserTraitName = 'confluence_is_established_user';
	const { hostname } = window.location;
	if (hostname.endsWith('.jira-dev.com')) {
		isEstablishedUserTraitName = 'confluence_is_established_user_stg';
	}
	if (
		experimentalUserPersonalization?.__typename === 'ExperimentalUserPersonalization' &&
		QUERY_OVERRIDE_KEYS.IS_ESTABLISHED_USER in overrides
	) {
		if (experimentalUserPersonalization?.attributes) {
			const filteredAttributes = experimentalUserPersonalization.attributes.filter(
				(i: any) => i.name !== isEstablishedUserTraitName,
			);

			experimentalUserPersonalization.attributes = [
				...filteredAttributes,
				{
					name: isEstablishedUserTraitName,
					value: (overrides[QUERY_OVERRIDE_KEYS.IS_ESTABLISHED_USER] === 'true').toString(),
					__typename: 'ExperimentalUserPersonalizationAttributes',
				},
			];
		}
	}
};

const applyOnboardingStateOverride = (
	{ data }: Record<string, any>,
	overrides: Record<string, any>,
): void => {
	if (data.onboardingState) {
		data.onboardingState = data.onboardingState.map((state: any) => {
			const { __typename, key } = state;
			if (__typename === 'OnboardingState' && key in overrides) {
				return { ...state, value: overrides[key].toString() };
			}
			return state;
		});
	}
};

export const queryOverridesLink = () => {
	let overrides: Record<string, any>;
	return new ApolloLink((operation, forward) =>
		forward(operation).map((response) => {
			if (response?.data && localStorage) {
				if (!overrides) {
					try {
						overrides = JSON.parse(localStorage.getItem(keys.QUERY_OVERRIDES)) || {};
					} catch {
						overrides = {};
					}
				}

				if (Object.keys(overrides).length) {
					if (
						operation.query.definitions.some(
							(definition) => 'operation' in definition && definition.operation === 'query',
						)
					) {
						applyQueryOverrides(response, overrides);
					}

					applyMigratedUserOverride(response, overrides);
					applyEstablishedUserOverride(response, overrides);
					applyOnboardingStateOverride(response, overrides);
				}
			}
			return response;
		}),
	);
};
